import { useMemo } from 'react';

import { useLocation } from 'react-router-dom';

import type { MeMenu, MeMenuCategory } from '@/pages/user-permission/api';
import {
  useMyCompaniesQuery,
  useMyMenusQuery,
} from '@/pages/user-permission/api';

// 사이드메뉴와 동일한 규칙으로 path 를 정규화(앞 슬래시 보정).
const normalizePath = (path: string) =>
  path.startsWith('/') ? path : `/${path}`;

// 계층형 카테고리 트리를 평면 메뉴 목록으로 펼친다.
const flattenMenus = (categories: MeMenuCategory[]): MeMenu[] =>
  categories.flatMap((category) => [
    ...category.menus,
    ...flattenMenus(category.children ?? []),
  ]);

/**
 * 현재 라우트에 해당하는 메뉴의 menuId 를 /me/menus 트리에서 찾는다.
 * 접근 권한이 없거나 아직 로딩 중이면 null.
 */
export const useCurrentMenuId = (): number | null => {
  const { pathname } = useLocation();
  const { data } = useMyMenusQuery();

  return useMemo(() => {
    if (!data) return null;
    const menu = flattenMenus(data).find(
      (item) => item.path && normalizePath(item.path) === pathname,
    );
    return menu?.menuId ?? null;
  }, [data, pathname]);
};

/**
 * 현재 화면(=현재 라우트의 메뉴)에서 조회 가능한 법인 목록.
 * 빈 배열이면 이 메뉴로는 아무 법인도 조회할 수 없다는 뜻.
 */
export const useMenuCompanies = () => {
  const menuId = useCurrentMenuId();
  const query = useMyCompaniesQuery(menuId);
  return { menuId, ...query };
};
